import { checkHandler } from './validation'

/**
 * Timer factory function.
 * The Timer imitates the Console's Time APIs.
 * Ref: https://developer.mozilla.org/en-US/docs/Web/API/console#Timers
 * @method createTimer
 * @param  {Function} log  Function for logging timer information.
 * @param  {Function} warn Function for warning about timer misuse.
 * @return {Timer}
 */
export default function createTimer (log, warn) {
  // Validate that params are a function.
  //    Uses functions as `log(message, data)`.
  checkHandler(log)
  checkHandler(warn)

  /**
   * Mapping of on-going timers; <name>: <start>.
   * @type {Object}
   */
  const timers = {}

  /**
   * Starts a timer. Logs a message to indicate it was started.
   * Logs a warning if the timer has already been started.
   * @method timeStart
   * @param  {string} name Name to identify the timer.
   */
  function time (name) {
    const now = Date.now()
    // Base info about this timer call.
    const data = {
      event: 'time',
      name
    }

    if (timers.hasOwnProperty(name)) {
      data.start = timers[name]
      warn(`Timer ${name} already started.`, data)
    } else {
      // Set the new timer.
      timers[name] = now

      data.start = now
      log(`Timer ${name} started.`, data)
    }
  }

  /**
   * Ends a timer. Logs a message with the elapsed time.
   * @method timeEnd
   * @param  {string} name Name to identify the timer.
   */
  function timeEnd (name) {
    const now = Date.now()
    // Base info about this timer call.
    const data = {
      event: 'timeEnd',
      name,
      start: timers[name]
    }

    if (timers.hasOwnProperty(name)) {
      const start = timers[name]
      // End the timer.
      delete timers[name]

      const elapsed = now - start
      data.end = now
      data.elapsed = elapsed

      log(`Timer ${name} ended, taking ${elapsed}ms.`, data)
    } else {
      warn(`Timer ${name} has not been started.`, data)
    }
  }

  /**
   * Adds a split to the timer. Logs a message with the elapsed time so far.
   * Logs a warning if the timer has not been started.
   * @method timeSplit
   * @param  {string} name Name to identify the timer.
   */
  function timeLog (name) {
    const now = Date.now()
    // Base info about this timer call.
    const data = {
      event: 'timeLog',
      name,
      start: timers[name]
    }

    if (timers.hasOwnProperty(name)) {
      const elapsed = now - timers[name]
      data.split = now
      data.elapsed = elapsed

      log(`Timer ${name} split, at ${elapsed}ms so far.`, data)
    } else {
      warn(`Timer ${name} has not been started.`, data)
    }
  }

  return {
    time,
    timeEnd,
    timeLog
  }
}
